import React from 'react'
import { Trans } from '@lingui/react'
import { observer, useLocalStore } from 'mobx-react-lite'
import { useRouter } from 'next/router'
import {
  GridCol,
  Input,
  DialogHeader,
  DialogContent,
  Grid,
  DialogFooter,
  ButtonLayout,
  Button,
  ButtonPrimary,
  ReachDialog,
  Message,
} from '../../ui-kit'
import { fieldToInputProps } from '../../utils/form/fieldToInputProps'
import { _, i18n } from '../../utils/intl'
import { EnduserChangePasswordForm } from './EnduserChangePasswordForm'
import { log } from '../../utils/log'
import { to } from '../../utils/awaitTo'
import { ApiUsersRepository } from '../../api/ApiUsersRepository'
import { Auth } from './Auth'

export const ModalChangePassword: React.FC<{
  close: () => void
}> = observer(({ close }) => {
  const router = useRouter()

  const state = useLocalStore(() => ({
    form: new EnduserChangePasswordForm(),
    saving: false,
    async save() {
      await this.form.form.validate()

      if (this.form.fields.new_password.value !== this.form.fields.new_password_confirm.value) {
        return
      }

      if (this.form.form.hasError) {
        log(this.form.form.error)
        return
      }

      const { userId } = Auth.decodeToken(Auth.getAccessToken()!)

      this.saving = true
      const { new_password, old_password } = this.form.serialize()
      const [err] = await to(
        ApiUsersRepository.postUserChangePassword(userId, old_password, new_password)
      )

      if (!err) {
        await Auth.fetchNewAccessToken({ origin: 'ModalChangePassword' })
        await router.push('/')
        close()
      }
      this.saving = false
    },
  }))

  const { form } = state

  const notMatchingPasswordMessage = () => (
    <Message key={1} type="error" icon="message-error">
      <Trans>Hesla sa nezhoduju</Trans>
    </Message>
  )

  return (
    <ReachDialog size="large" onDismiss={close}>
      <DialogHeader title={i18n.t`Zmena hesla`} />
      <DialogContent>
        <Grid>
          <GridCol size={{ m: 10, l: 8 }} className="mb">
            <form>
              <Input
                id="old-pass"
                type="password"
                label={i18n.t`Staré heslo`}
                className="mb-smallish"
                withPasswordToggler
                isRequired
                {...fieldToInputProps(form.fields.old_password, (val: string) => val.trim())}
              />
              <Input
                id="new-pass"
                type="password"
                withPasswordToggler
                label={<Trans>Nové heslo</Trans>}
                isRequired
                {...fieldToInputProps(form.fields.new_password, (val: string) => val.trim())}
              />

              <Input
                id="new-pass-confirm"
                type="password"
                withPasswordToggler
                label={<Trans>Potvrdiť nové heslo</Trans>}
                isRequired
                {...fieldToInputProps(form.fields.new_password_confirm, (val: string) =>
                  val.trim()
                )}
              />
              {form.fields.new_password.value !== form.fields.new_password_confirm.value &&
                notMatchingPasswordMessage()}
            </form>
          </GridCol>
        </Grid>
      </DialogContent>
      <DialogFooter>
        <ButtonLayout direction="horizontal" className="align-items-right">
          <Button onClick={close}>
            <Trans>Zrušiť</Trans>
          </Button>
          <ButtonPrimary
            isDisabled={state.saving}
            onClick={() => {
              state.save()
            }}>
            <Trans>Zmeniť</Trans>
          </ButtonPrimary>
        </ButtonLayout>
      </DialogFooter>
    </ReachDialog>
  )
})

ModalChangePassword.displayName = 'ModalChangePassword'
